import { Select, type MenuItem } from "@cafe/ui";
import { useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/auth/store";
import { useMe } from "@/shared/hooks/session";

/** Филиалы пользователя, текущий и смена (данные зависят от филиала — перезапрашиваем всё, кроме профиля). */
export function useBranchSwitch(onChange?: (id: string) => void) {
  const me = useMe();
  const qc = useQueryClient();
  const { branchId, setBranch } = useAuth();
  const branches = me.data?.branches ?? [];
  const change = (id: string) => {
    if (id === branchId) return;
    setBranch(id);
    onChange?.(id);
    void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== "me" || q.queryKey[1] === "permissions" });
  };
  return { branches, branchId, change };
}

/** Выбор филиала — выпадающий список. */
export function BranchSelect({ className, onChange }: { className?: string; onChange?: (id: string) => void }) {
  const { t } = useTranslation();
  const { branches, branchId, change } = useBranchSwitch(onChange);
  if (branches.length === 0) return null;
  return (
    <Select
      size="sm"
      value={branchId}
      onValueChange={change}
      options={branches.map((b) => ({ value: b.id, label: b.name }))}
      className={className}
      placeholder={t("common.branch")}
      aria-label={t("common.branch")}
    />
  );
}

/** Пункты меню «Филиал» (телефон: вместо списка в шапке); при одном филиале — пусто. */
export function useBranchMenuItems(onChange?: (id: string) => void): MenuItem[] {
  const { t } = useTranslation();
  const { branches, branchId, change } = useBranchSwitch(onChange);
  if (branches.length < 2) return [];
  return [
    { type: "label", label: t("common.branch") },
    ...branches.map((b) => ({
      label: <span className="block max-w-56 truncate">{b.name}</span>,
      icon: b.id === branchId ? <Check /> : <span className="size-4" />,
      onSelect: () => change(b.id),
    })),
  ];
}
